import React, { useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { Menu, X, Mail, Phone } from 'lucide-react';
import Logo from './Logo';
import { business } from '../mock/mock';
import { Button } from './ui/button';

const homeProsLinks = [
  { to: '/', label: 'Home', end: true },
  { to: '/#services', label: 'Services' },
  { to: '/#process', label: 'How It Works' },
  { to: '/#why', label: 'Why Us' },
  { to: '/contact', label: 'Contact' }
];

const floorsLinks = [
  { to: '/floors', label: 'Home', end: true },
  { to: '/flooring', label: 'Flooring' },
  { to: '/mobile-showroom', label: 'Mobile Showroom' },
  { to: '/about', label: 'About' },
  { to: '/reviews', label: 'Reviews' },
  { to: '/contact', label: 'Contact' }
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const p = location.pathname;
  const isFloors = p === '/floors' || p.startsWith('/flooring') || p.startsWith('/mobile-showroom') || p.startsWith('/about') || p.startsWith('/reviews');
  const division = isFloors ? 'floors' : 'homepros';
  const links = isFloors ? floorsLinks : homeProsLinks;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname, location.hash]);

  const estimateTo = isFloors ? '/estimate' : '/contact';

  const Switcher = ({ mobile = false }) => (
    <div className={`inline-flex items-center rounded-full bg-brand-cream p-1 border border-brand-line ${mobile ? 'w-full' : ''}`}>
      <Link to="/" className={`${mobile ? 'flex-1 text-center' : ''} px-4 py-1.5 rounded-full text-[13px] font-semibold transition-colors ${!isFloors ? 'bg-brand-clay text-white shadow-sm' : 'text-brand-stone hover:text-brand-charcoal'}`}>
        Home Pros
      </Link>
      <Link to="/floors" className={`${mobile ? 'flex-1 text-center' : ''} px-4 py-1.5 rounded-full text-[13px] font-semibold transition-colors ${isFloors ? 'bg-brand-clay text-white shadow-sm' : 'text-brand-stone hover:text-brand-charcoal'}`}>
        Floors
      </Link>
    </div>
  );

  return (
    <>
      {/* top utility bar */}
      <div className="hidden md:block bg-brand-charcoal text-brand-ivory text-[13px]">
        <div className="max-w-7xl mx-auto px-6 h-9 flex items-center justify-between">
          <span className="tracking-wide text-brand-sand">Serving {business.serviceArea}</span>
          <div className="flex items-center gap-6">
            <a href={business.phoneHref} className="flex items-center gap-1.5 hover:text-brand-clay transition-colors">
              <Phone size={13} /> {business.phone}
            </a>
            <a href={business.emailHref} className="flex items-center gap-1.5 hover:text-brand-clay transition-colors">
              <Mail size={13} /> {business.email}
            </a>
            <Link to={estimateTo} className="text-brand-sand hover:text-brand-clay transition-colors">Free Estimate</Link>
          </div>
        </div>
      </div>

      <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-brand-ivory/95 backdrop-blur shadow-[0_1px_20px_rgba(33,55,57,0.08)]' : 'bg-brand-ivory'}`}>
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-[72px] gap-4">
          <div className="flex items-center gap-4">
            <Logo division={division} />
            <div className="hidden xl:block"><Switcher /></div>
          </div>

          <nav className="hidden lg:flex items-center gap-7">
            {links.map((l) => (
              l.to.includes('#') ? (
                <a key={l.to} href={l.to} className="nav-link text-[15px] font-medium tracking-wide text-brand-charcoal hover:text-brand-clay transition-colors">{l.label}</a>
              ) : (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  className={({ isActive }) => `nav-link text-[15px] font-medium tracking-wide transition-colors ${isActive ? 'active text-brand-clay' : 'text-brand-charcoal hover:text-brand-clay'}`}
                >
                  {l.label}
                </NavLink>
              )
            ))}
          </nav>

          <div className="hidden lg:block">
            <Button asChild className="bg-brand-clay hover:bg-brand-clayDark text-white rounded-full px-6 h-11 text-[15px] font-medium shadow-sm">
              <Link to={estimateTo}>Get a Free Estimate</Link>
            </Button>
          </div>

          <button className="lg:hidden text-brand-charcoal" onClick={() => setOpen(!open)} aria-label="Toggle menu">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {/* mobile drawer */}
        <div className={`lg:hidden overflow-hidden transition-all duration-300 border-t border-brand-line ${open ? 'max-h-[620px]' : 'max-h-0'}`}>
          <nav className="px-6 py-4 flex flex-col gap-1 bg-brand-ivory">
            <div className="pb-3"><Switcher mobile /></div>
            {links.map((l) => (
              l.to.includes('#') ? (
                <a key={l.to} href={l.to} onClick={() => setOpen(false)} className="py-3 text-[16px] font-medium border-b border-brand-line/60 text-brand-charcoal">{l.label}</a>
              ) : (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  className={({ isActive }) => `py-3 text-[16px] font-medium border-b border-brand-line/60 ${isActive ? 'text-brand-clay' : 'text-brand-charcoal'}`}
                >
                  {l.label}
                </NavLink>
              )
            ))}
            <a href={business.phoneHref} className="py-3 flex items-center gap-2 text-brand-charcoal font-medium">
              <Phone size={16} className="text-brand-clay" /> {business.phone}
            </a>
            <a href={business.emailHref} className="py-3 flex items-center gap-2 text-brand-charcoal font-medium">
              <Mail size={16} className="text-brand-clay" /> {business.email}
            </a>
            <Button asChild className="mt-3 bg-brand-clay hover:bg-brand-clayDark text-white rounded-full h-12">
              <Link to={estimateTo}>Get a Free Estimate</Link>
            </Button>
          </nav>
        </div>
      </header>
    </>
  );
}
